"use client";

import React, { useMemo, useCallback } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import { MoreVertical } from "lucide-react";
import { DropdownMenu, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { useFieldFiltering } from "@/lib/hooks/search/useFieldFiltering";
import {
  getCategoryForField,
  getPriorityForField,
  getMobilePriorityForField,
  getMobileTypeForField,
} from "./fieldRenderers";
import { renderCellValue } from "./utils";
import { resolveCardFaceFieldLayout } from "./utils/resolve-card-face-field-layout";
import { RowActionsDropdownMenu } from "./components/SimpleTable/RowActionsDropdownMenu";

import type {
  FieldValue,
  BaseEntity,
  SimpleTableMobileCardProps,
} from "./types";

export const SimpleTableMobileCard = React.memo(function SimpleTableMobileCard<
  T extends BaseEntity,
>({
  item,
  config,
  visibleColumnIds,
  hooks,
  onEdit,
  onDelete,
  onInlineEdit,
  onView,
  isSelected = false,
  onSelect,
  enableSelection = false,
  onOpenActions,
}: SimpleTableMobileCardProps<T> & { onOpenActions?: (item: T) => void }) {
  const { filterFieldsByRole } = useFieldFiltering();

  const fallbackColumnIds = useMemo(() => {
    const roleFiltered = filterFieldsByRole(config.fields);
    return Object.keys(config.fields).filter(
      (id) => roleFiltered[id] && !roleFiltered[id].formOnly
    );
  }, [config.fields, filterFieldsByRole]);

  const cardFaceLayout = useMemo(() => {
    return resolveCardFaceFieldLayout(config, visibleColumnIds ?? fallbackColumnIds);
  }, [config, visibleColumnIds, fallbackColumnIds]);

  const useDesktopCardFace =
    Boolean(cardFaceLayout.primaryField) ||
    Boolean(cardFaceLayout.secondaryField) ||
    cardFaceLayout.contentFields.length > 0;

  const mobileFields = useMemo(() => {
    const roleFilteredFields = filterFieldsByRole(config.fields);

    return Object.entries(roleFilteredFields)
      .filter(([_, field]) => !field.formOnly)
      .map(([key, field]) => ({
        id: key,
        label: field.label,
        category: getCategoryForField(key, field),
        visible: true,
        priority: getPriorityForField(key, field),
        mobilePriority: getMobilePriorityForField(key, field),
        mobileType: getMobileTypeForField(field),
        fieldConfig: field,
      }))
      .sort((a, b) => a.mobilePriority - b.mobilePriority);
  }, [config.fields, filterFieldsByRole]);

  const { primaryFields, secondaryFields, badgeFields } = useMemo(() => {
    const primary = mobileFields.filter((f) => f.mobileType === "primary").slice(0, 2);
    const secondary = mobileFields.filter((f) => f.mobileType === "secondary").slice(0, 4);
    const badge = mobileFields.filter((f) => f.mobileType === "badge").slice(0, 3);

    return {
      primaryFields: primary,
      secondaryFields: secondary,
      badgeFields: badge,
    };
  }, [mobileFields]);

  const handleCardClick = useCallback(() => {
    if (onView) {
      onView(item);
    }
  }, [onView, item]);

  const handleCheckboxChange = useCallback(
    (checked: boolean) => {
      if (onSelect) {
        onSelect(checked);
      }
    },
    [onSelect]
  );

  const hasUnifiedActions = Boolean(
    hooks && config.detailActions && config.detailActions.length > 0
  );

  const renderCardFaceValue = useCallback(
    (fieldId: string) => {
      const fieldConfig = config.fields[fieldId];
      if (!fieldConfig) return null;
      const value = item[fieldId as keyof T];
      return renderCellValue(value, fieldConfig, fieldId, false, {
        cardSurface: true,
        entityRecord: item as Record<string, unknown>,
        entityType: config.entity,
      });
    },
    [item, config.fields, config.entity]
  );

  const renderLegacyFieldValue = useCallback((field: { id: string; fieldConfig: (typeof config.fields)[string] }, value: unknown) => {
    return renderCellValue(value as FieldValue, field.fieldConfig, field.id, false, {
      cardSurface: true,
      entityRecord: item as Record<string, unknown>,
      entityType: config.entity,
    });
  }, [item, config.entity]);

  const getItemValue = useCallback(
    (fieldId: string): FieldValue | Record<string, unknown> | unknown[] | undefined => {
      return item[fieldId as keyof T];
    },
    [item]
  );

  return (
    <Card
      className={cn(
        "cursor-pointer transition-all duration-200 hover:shadow-md",
        isSelected && "ring-2 ring-blue-500 bg-blue-50 dark:bg-blue-950/40"
      )}
      onClick={handleCardClick}
    >
      <CardContent className="p-3">
        <div className="flex items-start gap-2">
          {enableSelection && (
            <div className="flex-shrink-0 pt-0.5">
              <Checkbox
                checked={isSelected}
                onCheckedChange={(checked) => {
                  handleCheckboxChange(checked === true);
                }}
                className="data-[state=checked]:bg-blue-600 data-[state=checked]:border-blue-600"
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}

          <div className="flex-1 min-w-0">
            {useDesktopCardFace ? (
              <div className="space-y-2">
                {cardFaceLayout.primaryField && config.fields[cardFaceLayout.primaryField] && (
                  <div className="min-w-0 text-sm font-semibold leading-tight text-foreground break-words">
                    {renderCardFaceValue(cardFaceLayout.primaryField)}
                  </div>
                )}
                {cardFaceLayout.secondaryField && config.fields[cardFaceLayout.secondaryField] && (
                  <div className="min-w-0 text-xs leading-snug text-muted-foreground break-words">
                    {renderCardFaceValue(cardFaceLayout.secondaryField)}
                  </div>
                )}
                {/* Always stack vertically on mobile — cardSpan col-span-* with grid-cols-1 creates
                    implicit tracks so short fields (email + phone) sit on one row and long values overlap. */}
                {cardFaceLayout.contentFields.length > 0 && (
                  <div className="flex flex-col gap-1.5 pt-0.5">
                    {cardFaceLayout.contentFields.map((fieldId) => {
                      const fieldConfig = config.fields[fieldId];
                      if (!fieldConfig) return null;
                      return (
                        <div key={fieldId} className="min-w-0 w-full flex items-baseline gap-2">
                          <div className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground shrink-0">
                            {fieldConfig.label}
                          </div>
                          <div className="min-w-0 flex-1 text-sm break-words text-foreground text-right">
                            {renderCardFaceValue(fieldId)}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            ) : (
              <>
                {primaryFields.length > 0 && (
                  <div className="space-y-1 mb-2">
                    {primaryFields.map((field) => {
                      const value = getItemValue(field.id);
                      return (
                        <div key={field.id} className="min-w-0 break-words text-sm font-semibold leading-tight text-card-foreground">
                          {renderLegacyFieldValue(field, value)}
                        </div>
                      );
                    })}
                  </div>
                )}

                {badgeFields.length > 0 && (
                  <div className="flex gap-1.5 mb-2 flex-wrap">
                    {badgeFields.map((field) => {
                      const value = getItemValue(field.id);
                      if (!value) return null;

                      return <div key={field.id}>{renderLegacyFieldValue(field, value)}</div>;
                    })}
                  </div>
                )}

                {secondaryFields.length > 0 && (
                  <div className="grid grid-cols-2 gap-2 text-xs text-gray-600 dark:text-gray-400">
                    {secondaryFields.map((field) => {
                      const value = getItemValue(field.id);
                      return (
                        <div key={field.id} className="min-w-0">
                          <span className="font-medium">{field.label}:</span>{" "}
                          <span className="break-words">{renderLegacyFieldValue(field, value)}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>

          {hasUnifiedActions && hooks ? (
            <div className="flex-shrink-0 flex items-center gap-2">
              {onOpenActions ? (
                // Mobile: open a bottom sheet with large tap rows (44px+).
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-11 w-11 p-0 hover:bg-card-100 sm:h-9 sm:w-9"
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenActions(item);
                  }}
                  title="More options"
                  aria-label="More options"
                >
                  <MoreVertical className="h-5 w-5" />
                </Button>
              ) : (
                // Fallback (no sheet host): the original anchored dropdown.
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-11 w-11 p-0 hover:bg-card-100 sm:h-9 sm:w-9"
                      onClick={(e) => e.stopPropagation()} title="More options"
                    >
                      <MoreVertical className="h-5 w-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <RowActionsDropdownMenu
                    item={item}
                    actions={config.detailActions ?? []}
                    hooks={hooks}
                    entityName={config.entity}
                    config={config}
                  />
                </DropdownMenu>
              )}
            </div>
          ) : null}
        </div>
      </CardContent>
    </Card>
  );
}) as <T extends { id: string }>(
  props: SimpleTableMobileCardProps<T> & { onOpenActions?: (item: T) => void }
) => React.ReactElement;
